import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, MapPin, Cloud, ChevronRight, Filter, Calendar as CalendarIcon, Globe, AlertTriangle, Trophy } from 'lucide-react';
import axios from 'axios';
import { format, parseISO, addDays } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_COUNTRIES = [
    { code: 'UK', name: 'United Kingdom' },
    { code: 'IRE', name: 'Ireland' },
    { code: 'FR', name: 'France' },
    { code: 'USA', name: 'United States' },
    { code: 'ZA', name: 'South Africa' },
];

// Helper to format going condition
const formatGoing = (going) => {
    if (!going || going === 'Unknown') return 'Unknown';
    return going
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .replace('To ', 'to ');
};

const MeetingCard = ({ meeting, isAbandoned }) => {
    const going = formatGoing(meeting.meta?.['stage:going']?.value);
    const weather = meeting.meta?.['stage:weather']?.value;
    const raceCount = meeting.events?.length || 0;
    const firstRace = meeting.events?.[0];

    return (
        <Card className={`card-hover ${isAbandoned ? 'opacity-60' : ''}`} data-testid={`meeting-card-${meeting.id}`}>
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-heading">{meeting.name}</CardTitle>
                    <div className="flex gap-2">
                        {isAbandoned && (
                            <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Abandoned
                            </Badge>
                        )}
                        <Badge variant="secondary" className="font-data">
                            {raceCount} races
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isAbandoned ? (
                    <div className="text-sm text-muted-foreground p-3 bg-destructive/10 rounded-lg">
                        <p>This meeting has been abandoned due to adverse conditions.</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {firstRace && format(parseISO(firstRace.start), 'HH:mm')}
                            </div>
                            <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {going}
                            </div>
                            {weather && (
                                <div className="flex items-center gap-1">
                                    <Cloud className="h-4 w-4" />
                                    {weather}
                                </div>
                            )}
                        </div>
                        
                        {/* Individual Races */}
                        <div className="space-y-2">
                            {meeting.events?.map((event, idx) => {
                                const isFinished = event.status?.includes('WEIGHED_IN') || event.status?.includes('RESULT');
                                // Get top 3 finishers from entrants with results
                                const finishers = (event.entrants || [])
                                    .filter(e => e.result?.position)
                                    .sort((a, b) => a.result.position - b.result.position)
                                    .slice(0, 3);
                                
                                return (
                                    <Link
                                        key={event.id}
                                        to={`/race/${event.id}`}
                                        state={{ courseName: meeting.name }}
                                        className="block"
                                        data-testid={`race-link-${event.id}`}
                                    >
                                        <div className={`p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group ${isFinished ? 'border-l-2 border-amber-500' : ''}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className={`font-data text-lg font-semibold w-14 ${isFinished ? 'text-amber-500' : 'text-primary'}`}>
                                                        {format(parseISO(event.start), 'HH:mm')}
                                                    </span>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium text-sm line-clamp-1">{event.title}</p>
                                                            {isFinished && (
                                                                <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/30">
                                                                    Result
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            {event.meta?.['event:class']?.value || `Race ${idx + 1}`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                                            </div>
                                            
                                            {/* Show top 3 finishers for finished races */}
                                            {isFinished && finishers.length > 0 && (
                                                <div className="mt-2 pt-2 border-t border-border/50">
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {finishers.map((entrant, i) => {
                                                            const positionColors = [
                                                                'bg-amber-500 text-black',
                                                                'bg-gray-400 text-black',
                                                                'bg-amber-700 text-white'
                                                            ];
                                                            const positionLabels = ['1st', '2nd', '3rd'];
                                                            const sp = entrant.result?.sp?.decimal;
                                                            const spFrac = entrant.result?.sp?.fractional;
                                                            
                                                            return (
                                                                <div 
                                                                    key={entrant.id}
                                                                    className={`p-2 rounded ${i === 0 ? 'bg-amber-500/10' : 'bg-background/50'}`}
                                                                >
                                                                    <div className="flex items-center gap-1.5 mb-1">
                                                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${positionColors[i]}`}>
                                                                            {i + 1}
                                                                        </span>
                                                                        <span className="text-[10px] text-muted-foreground">{positionLabels[i]}</span>
                                                                        {i === 0 && <Trophy className="h-3 w-3 text-amber-500" />}
                                                                    </div>
                                                                    <p className="font-medium text-xs truncate" title={entrant.name}>
                                                                        {entrant.name}
                                                                    </p>
                                                                    {(spFrac || sp) && (
                                                                        <p className="text-[10px] text-muted-foreground font-data">
                                                                            SP: {spFrac || sp?.toFixed(2)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default function RacesPage() {
    const { stageId } = useParams();
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedCountry, setSelectedCountry] = useState('UK');
    const [disabledCountries, setDisabledCountries] = useState([]);

    // Fetch site settings to get disabled countries
    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const res = await axios.get(`${API}/site-settings/public`);
                const disabled = res.data.disabled_countries || [];
                setDisabledCountries(disabled);
            } catch (err) {
                console.error('Failed to fetch site settings:', err);
            }
        };
        fetchSiteSettings();
    }, []);

    // If currently selected country becomes disabled, switch to first available
    useEffect(() => {
        if (disabledCountries.length > 0 && disabledCountries.includes(selectedCountry)) {
            const firstAvailable = ALL_COUNTRIES.find(c => !disabledCountries.includes(c.code));
            if (firstAvailable) {
                setSelectedCountry(firstAvailable.code);
            }
        }
    }, [disabledCountries, selectedCountry]);

    // Filter countries based on site settings
    const availableCountries = useMemo(() => {
        return ALL_COUNTRIES.filter(c => !disabledCountries.includes(c.code));
    }, [disabledCountries]);

    useEffect(() => {
        const fetchMeetings = async () => {
            setLoading(true);
            try {
                const dateStr = format(selectedDate, 'dd-MM-yyyy');
                const response = await axios.get(`${API}/racing/stages`, {
                    params: {
                        from_date: dateStr,
                        to_date: dateStr,
                        country: selectedCountry
                    }
                });
                
                const items = response.data.items || [];
                
                // Check which meetings are abandoned - look for ABANDONED status in stage or events
                const isAbandoned = (meeting) => {
                    // First check if the stage itself is marked as abandoned
                    if (meeting.isAbandoned) return true;
                    
                    // Check stage status
                    const stageStatus = (meeting.status || '').toUpperCase();
                    if (stageStatus.includes('ABANDONED')) return true;
                    
                    // Check if all events are abandoned
                    const events = meeting.events || [];
                    if (events.length === 0) return false;
                    const abandonedStatuses = ['ABANDONED', 'EVENT_ABANDONED', 'MEETING_ABANDONED'];
                    return events.every(e => 
                        e.isAbandoned || abandonedStatuses.some(s => (e.status || '').toUpperCase().includes(s))
                    );
                };
                
                // Sort: active first, then abandoned
                const sortedMeetings = [...items].sort((a, b) => {
                    const aAbandoned = isAbandoned(a);
                    const bAbandoned = isAbandoned(b);
                    if (aAbandoned && !bAbandoned) return 1;
                    if (!aAbandoned && bAbandoned) return -1;
                    return 0;
                });
                
                // Mark each meeting with abandoned status
                const meetingsWithStatus = sortedMeetings.map(m => ({
                    ...m,
                    isAbandoned: isAbandoned(m)
                }));
                
                setMeetings(meetingsWithStatus);
            } catch (error) {
                console.error('Failed to fetch meetings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMeetings();
    }, [selectedDate, selectedCountry]);

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold page-title">Races</h1>
                        <p className="text-muted-foreground">
                            Browse today&apos;s meetings and racecards
                        </p>
                    </div>
                    
                    {/* Filters */}
                    <div className="flex items-center gap-3">
                        {/* Date Picker */}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="gap-2" data-testid="date-picker">
                                    <CalendarIcon className="h-4 w-4" />
                                    {format(selectedDate, 'EEE, MMM d')}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="end">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => date && setSelectedDate(date)}
                                    disabled={(date) => date < addDays(new Date(), -7) || date > addDays(new Date(), 7)}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>

                        {/* Country Select */}
                        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                            <SelectTrigger className="w-[160px]" data-testid="country-select">
                                <Globe className="h-4 w-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {availableCountries.map(country => (
                                    <SelectItem key={country.code} value={country.code}>
                                        {country.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Quick Date Navigation */}
                <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
                    {[-1, 0, 1, 2, 3].map(offset => {
                        const date = addDays(new Date(), offset);
                        const isSelected = format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                        return (
                            <Button
                                key={offset}
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setSelectedDate(date)}
                                className="shrink-0"
                                data-testid={`quick-date-${offset}`}
                            >
                                {offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : format(date, 'EEE d')}
                            </Button>
                        );
                    })}
                </div>

                {/* Meetings Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {[1, 2, 3].map(i => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-32" />
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : meetings.length === 0 ? (
                    <Card className="text-center py-16">
                        <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-heading text-lg font-semibold mb-2">No meetings found</h3>
                        <p className="text-muted-foreground">
                            No races scheduled for {format(selectedDate, 'EEEE, MMMM d')} in {selectedCountry}
                        </p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {meetings.map((meeting, idx) => (
                            <div 
                                key={meeting.id} 
                                className="animate-fade-in"
                                style={{ animationDelay: `${idx * 100}ms` }}
                            >
                                <MeetingCard meeting={meeting} isAbandoned={meeting.isAbandoned} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
